import React, { useEffect, useRef, useState } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'

import { MEDICAL_RECORD_SUBTYPES } from '../../utils/Constants'

const isCaseReportPurpose = purpose => {
  return ['Initial Case Report', 'Case Management Progress Report', 'Final Case Report'].includes(purpose)
}

const formatDateForInput = dateStr => {
  if (!dateStr) return ''
  return String(dateStr).substring(0, 10)
}

const FileMetadataModal = ({
  show,
  onHide,
  onConfirm,
  defaultValues = {},
  title = 'File Details',
  confirmText = 'Upload',
}) => {
  const descRef = useRef(null)

  const [metadata, setMetadata] = useState({
    purpose: '',
    subtype: '',
    source: '',
    version: '',
    description: '',
    sequenceNumber: '',
    startDate: '',
    endDate: '',
    reportStatus: 'Filed',
  })

  useEffect(() => {
    if (show) {
      const purpose = defaultValues['Document Purpose'] || defaultValues.purpose || 'Medical Record'
      const subtype = defaultValues['Document Subtype'] || defaultValues.subtype || defaultValues.documentSubtype || ''
      const source = defaultValues['Record Source'] || defaultValues.source || ''
      const version = defaultValues.Version || defaultValues.version || '1.0'
      const description = defaultValues.Description || defaultValues.description || ''
      const sequenceNumber =
        defaultValues['Sequence Number'] ||
        defaultValues.sequenceNumber ||
        defaultValues['Progress Report Number'] ||
        ''
      const startDate = formatDateForInput(
        defaultValues['Start Date'] || defaultValues.startDate || defaultValues.fromDateTimeUtc || '',
      )
      const endDate = formatDateForInput(
        defaultValues['End Date'] || defaultValues.endDate || defaultValues.toDateTimeUtc || '',
      )
      const rawStatus = defaultValues['Report Status'] || defaultValues.reportStatus || defaultValues.status || 'Filed'
      const sNorm = String(rawStatus)
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
      let reportStatus = rawStatus
      if (['submitted', 'sent-to-administrator', 'sent-to-reviewer', 'in-review', 'under-review'].includes(sNorm)) {
        reportStatus = 'Sent to Administrator'
      } else if (['in-progress', 'inprogress', 'draft'].includes(sNorm)) {
        reportStatus = 'In Progress'
      } else if (['in-smart-review', 'insmartreview'].includes(sNorm)) {
        reportStatus = 'In Smart Review'
      } else if (['approved', 'saved-and-completed'].includes(sNorm)) {
        reportStatus = 'Approved'
      } else if (sNorm === 'invoiced') {
        reportStatus = 'Invoiced'
      } else if (sNorm === 'filed') {
        reportStatus = 'Filed'
      }

      setMetadata({
        purpose,
        subtype,
        source,
        version,
        description,
        sequenceNumber,
        startDate,
        endDate,
        reportStatus,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const handleChange = (field, value) => {
    setMetadata(prev => {
      const updated = { ...prev, [field]: value }
      if (field === 'purpose' && value !== 'Medical Record') {
        updated.subtype = ''
      }
      return updated
    })
  }

  const handleSubmit = () => {
    const payload = {
      'Document Purpose': metadata.purpose,
      'Record Source': metadata.source,
      Version: metadata.version,
      Description: metadata.description,
    }

    if (metadata.purpose === 'Medical Record') {
      payload['Document Subtype'] = metadata.subtype || ''
    }

    if (isCaseReportPurpose(metadata.purpose)) {
      if (metadata.sequenceNumber) {
        payload['Sequence Number'] = String(metadata.sequenceNumber)
      }
      if (metadata.startDate) {
        payload['Start Date'] = metadata.startDate
      }
      if (metadata.endDate) {
        payload['End Date'] = metadata.endDate
      }
      if (metadata.reportStatus) {
        payload['Report Status'] = metadata.reportStatus
      }
    }

    onConfirm(payload)
  }

  const isCaseReport = isCaseReportPurpose(metadata.purpose)

  return (
    <Modal show={show} onHide={onHide} centered onEntered={() => descRef.current?.focus()}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="fileDescription">
            <Form.Label>
              Description <small className="text-muted">(Optional)</small>
            </Form.Label>
            <Form.Control
              ref={descRef}
              as="textarea"
              rows={3}
              value={metadata.description || ''}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="Enter a brief description..."
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group controlId="documentPurpose">
                <Form.Label>Document Purpose</Form.Label>
                <Form.Control
                  as="select"
                  value={metadata.purpose || ''}
                  onChange={e => handleChange('purpose', e.target.value)}
                >
                  <option value="">Select Purpose...</option>
                  <option value="Medical Record">Medical Record</option>
                  <option value="Correspondence">Correspondence</option>
                  <option value="Legal Document">Legal Document</option>
                  <option value="Invoice/Bill">Invoice/Bill</option>
                  <option value="Report">Report</option>
                  <option value="Initial Case Report">Initial Case Report</option>
                  <option value="Case Management Progress Report">Case Management Progress Report</option>
                  <option value="Final Case Report">Final Case Report</option>
                  <option value="Reviewer CV">Reviewer CV</option>
                  <option value="Other">Other</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="recordSource">
                <Form.Label>Record Source</Form.Label>
                <Form.Control
                  as="select"
                  value={metadata.source || ''}
                  onChange={e => handleChange('source', e.target.value)}
                >
                  <option value="">Select Source...</option>
                  <option value="Referral Source">Referral Source</option>
                  <option value="Client">Client</option>
                  <option value="Provider">Provider</option>
                  <option value="Opposing Counsel">Opposing Counsel</option>
                  <option value="Court">Court</option>
                  <option value="Internal">Internal</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {metadata.purpose === 'Medical Record' && (
            <Form.Group controlId="documentSubtype">
              <Form.Label>Medical Record Subtype</Form.Label>
              <Form.Control
                as="select"
                value={metadata.subtype || ''}
                onChange={e => handleChange('subtype', e.target.value)}
              >
                <option value="">Select Subtype (Optional)...</option>
                {MEDICAL_RECORD_SUBTYPES.map(st => (
                  <option key={st.value} value={st.value}>
                    {st.label} — {st.description}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          )}

          {isCaseReport && (
            <>
              {metadata.purpose === 'Case Management Progress Report' && (
                <Form.Group controlId="sequenceNumber">
                  <Form.Label>Progress Report Number</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="e.g. 1, 2, 3"
                    value={metadata.sequenceNumber || ''}
                    onChange={e => handleChange('sequenceNumber', e.target.value)}
                  />
                </Form.Group>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group controlId="reportStartDate">
                    <Form.Label>Activity Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={metadata.startDate || ''}
                      onChange={e => handleChange('startDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="reportEndDate">
                    <Form.Label>Activity End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={metadata.endDate || ''}
                      onChange={e => handleChange('endDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="reportStatus">
                <Form.Label>Report Status</Form.Label>
                <Form.Control
                  as="select"
                  value={metadata.reportStatus || 'Filed'}
                  onChange={e => handleChange('reportStatus', e.target.value)}
                >
                  <option value="Filed">Filed</option>
                  <option value="Invoiced">Invoiced</option>
                  <option value="Approved">Approved</option>
                  <option value="Sent to Administrator">Sent to Administrator</option>
                  <option value="In Smart Review">In Smart Review</option>
                  <option value="In Progress">In Progress</option>
                </Form.Control>
              </Form.Group>
            </>
          )}

          <Form.Group controlId="documentVersion">
            <Form.Label>Version / Date</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Final, Draft, 2023-10-01"
              value={metadata.version || ''}
              onChange={e => handleChange('version', e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default FileMetadataModal
